import { useState } from 'react';
import {
  TECHS_BY_ID,
  affordableBuildingCount,
  buildingCost,
  canAfford,
  visibleBuildings,
} from '../../engine';
import { gameStore } from '../../game/store';
import type { Snapshot } from '../../game/store';
import { CostChips, EffectChips } from '../Chips';
import { Glyph } from '../Glyph';

type BuyQuantity = 1 | 10 | 'max';

interface BuildingsPanelProps {
  snapshot: Snapshot;
}

export function BuildingsPanel({ snapshot }: BuildingsPanelProps) {
  const [quantity, setQuantity] = useState<BuyQuantity>(1);
  const { state } = snapshot;
  const buildings = visibleBuildings(state).filter((view) => view.unlocked || view.owned > 0);

  if (buildings.length === 0) {
    return (
      <section className="panel">
        <h2 className="panel-title">Camp</h2>
        <p className="empty-note">Nothing can be raised yet. Learn something first.</p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2 className="panel-title">
        Camp
        <span className="tabs" style={{ margin: 0 }}>
          {([1, 10, 'max'] as BuyQuantity[]).map((option) => (
            <button
              key={String(option)}
              type="button"
              className={`tab${quantity === option ? ' active' : ''}`}
              onClick={() => setQuantity(option)}
            >
              {option === 'max' ? 'Max' : `×${option}`}
            </button>
          ))}
        </span>
      </h2>

      <div className="card-grid">
        {buildings.map((view) => {
          const wanted =
            quantity === 'max'
              ? Math.max(1, affordableBuildingCount(state, view.def.id))
              : quantity;
          const cost = buildingCost(state, view.def.id, wanted);
          const affordable = canAfford(state, cost) && !view.atMax;
          const requiredTech = view.def.requiresTech
            ? TECHS_BY_ID[view.def.requiresTech]?.name ?? view.def.requiresTech
            : null;

          return (
            <article
              key={view.def.id}
              className={`card${view.owned > 0 ? ' owned' : ''}${
                affordable ? ' affordable' : ''
              }${view.unlocked ? '' : ' locked'}`}
            >
              <div className="card-head">
                <Glyph id={view.def.glyph} size={24} tone={view.owned > 0 ? 'moss' : 'ochre'} />
                <span className="card-name">{view.def.name}</span>
                <span className="card-count">{view.owned}</span>
              </div>

              <p className="card-desc">{view.def.description}</p>
              <EffectChips effects={view.def.effects} />

              {view.unlocked ? (
                <>
                  <CostChips cost={cost} held={state.resources} />
                  {view.atMax ? (
                    <p className="card-note">The camp has all it can hold.</p>
                  ) : null}
                  <div className="card-actions">
                    <button
                      type="button"
                      className={`btn${affordable ? ' primary' : ''}`}
                      disabled={!affordable}
                      onClick={() =>
                        quantity === 'max'
                          ? gameStore.buildMax(view.def.id)
                          : gameStore.build(view.def.id, quantity)
                      }
                    >
                      Raise {quantity === 'max' ? `×${wanted}` : `×${wanted}`}
                    </button>
                  </div>
                </>
              ) : (
                <p className="card-note">Requires {requiredTech}</p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
